import type { ReportSpecificFilter, ReportTableColumn } from '../report-runtime';

export interface DriverAssignmentDetailRow {
  provider_id: string;
  provider_code: string;
  provider_name: string;
  row_key: string;
  row_kind: string;
  trip_id: string;
  trip_ref: string;
  trip_status: string;
  service_date: string;
  service_date_basis: string;
  event_at: string;
  date_basis: string;
  offer_id: string | null;
  assignment_event_id: string | null;
  assignment_event_kind: string | null;
  offer_kind: string | null;
  offer_status: string | null;
  agent_id: string | null;
  agent_code: string | null;
  agent_name: string | null;
  agent_phone: string | null;
  dispatch_mode: string | null;
  strategy: string | null;
  search_id: string | null;
  search_number: number | null;
  search_purpose: string | null;
  search_state: string | null;
  attempt_id: string | null;
  attempt_revision: number | null;
  attempt_schema_version: number | null;
  attempt_profile: string | null;
  attempt_trigger: string | null;
  attempt_computed_at: string | null;
  attempt_area_id: string | null;
  candidate_evaluation_id: string | null;
  policy_version_id: string | null;
  policy_source: string | null;
  policy_scope_id: string | null;
  policy_version_ids: string[] | null;
  policy_sources: string[] | null;
  offer_round: number | null;
  offer_sequence: number | null;
  offer_rank: number | null;
  candidate_score: number | null;
  distance_to_pickup_km: number | null;
  offered_at: string | null;
  expires_at: string | null;
  responded_at: string | null;
  confirmed_at: string | null;
  reason_id: string | null;
  reason_code: string | null;
  reason_label: string | null;
  reason_note: string | null;
  initiated_by_id: string | null;
  confirmation_actor_type: string | null;
  confirmation_actor_id: string | null;
  confirmation_actor_name: string | null;
  from_agent_id: string | null;
  to_agent_id: string | null;
  confirmation_outcome: string;
  evidence_state: string;
  evidence_expired_at: string | null;
  scope_provenance: string;
}

export interface DriverAssignmentDetailFilters {
  rowKinds?: ('OFFER' | 'ASSIGNMENT_EVENT')[];
  offerKinds?: ('LEGACY_HISTORY' | 'AUTO_INITIAL' | 'MANUAL_INITIAL' | 'MANUAL_REASSIGNMENT')[];
  offerStatuses?: ('PENDING' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'SUPERSEDED' | 'CANCELLED')[];
  dispatchModes?: ('AUTO' | 'MANUAL')[];
  strategies?: ('SEQUENTIAL' | 'BROADCAST')[];
  searchPurposes?: ('INITIAL' | 'REASSIGNMENT')[];
  evidenceStates?: ('LEGACY_HISTORY' | 'AVAILABLE' | 'MISSING' | 'EXPIRED' | 'UNSUPPORTED')[];
  agentIds?: string[];
  searchId?: string;
  reasonCode?: string;
}

export const driverAssignmentDetailColumns = [
  {
    id: 'provider_id',
    type: 'text',
    labelKey: 'da_col_provider_id',
    source: 'da_source_provider_id',
    emptyMeaning: 'da_null_provider_id',
  },
  {
    id: 'provider_code',
    type: 'text',
    labelKey: 'da_col_provider_code',
    source: 'da_source_provider_code',
    emptyMeaning: 'da_null_provider_code',
  },
  {
    id: 'provider_name',
    type: 'text',
    labelKey: 'da_col_provider_name',
    source: 'da_source_provider_name',
    emptyMeaning: 'da_null_provider_name',
  },
  {
    id: 'row_key',
    type: 'text',
    labelKey: 'da_col_row_key',
    source: 'da_source_row_key',
    emptyMeaning: 'da_null_row_key',
  },
  {
    id: 'row_kind',
    type: 'text',
    labelKey: 'da_col_row_kind',
    source: 'da_source_row_kind',
    emptyMeaning: 'da_null_row_kind',
  },
  {
    id: 'trip_id',
    type: 'text',
    labelKey: 'da_col_trip_id',
    source: 'da_source_trip_id',
    emptyMeaning: 'da_null_trip_id',
  },
  {
    id: 'trip_ref',
    type: 'text',
    labelKey: 'da_col_trip_ref',
    source: 'da_source_trip_ref',
    emptyMeaning: 'da_null_trip_ref',
  },
  {
    id: 'trip_status',
    type: 'text',
    labelKey: 'da_col_trip_status',
    source: 'da_source_trip_status',
    emptyMeaning: 'da_null_trip_status',
  },
  {
    id: 'service_date',
    type: 'timestamp',
    labelKey: 'da_col_service_date',
    source: 'da_source_service_date',
    emptyMeaning: 'da_null_service_date',
    meaningKey: 'da_meaning_service_date',
  },
  {
    id: 'service_date_basis',
    type: 'text',
    labelKey: 'da_col_service_date_basis',
    source: 'da_source_service_date_basis',
    emptyMeaning: 'da_null_service_date_basis',
  },
  {
    id: 'event_at',
    type: 'timestamp',
    labelKey: 'da_col_event_at',
    source: 'da_source_event_at',
    emptyMeaning: 'da_null_event_at',
    sortable: true,
    sortKey: 'eventAt',
  },
  {
    id: 'date_basis',
    type: 'text',
    labelKey: 'da_col_date_basis',
    source: 'da_source_date_basis',
    emptyMeaning: 'da_null_date_basis',
  },
  {
    id: 'offer_id',
    type: 'text',
    labelKey: 'da_col_offer_id',
    source: 'da_source_offer_id',
    emptyMeaning: 'da_null_offer_id',
  },
  {
    id: 'assignment_event_id',
    type: 'text',
    labelKey: 'da_col_assignment_event_id',
    source: 'da_source_assignment_event_id',
    emptyMeaning: 'da_null_assignment_event_id',
  },
  {
    id: 'assignment_event_kind',
    type: 'text',
    labelKey: 'da_col_assignment_event_kind',
    source: 'da_source_assignment_event_kind',
    emptyMeaning: 'da_null_assignment_event_kind',
  },
  {
    id: 'offer_kind',
    type: 'text',
    labelKey: 'da_col_offer_kind',
    source: 'da_source_offer_kind',
    emptyMeaning: 'da_null_offer_kind',
  },
  {
    id: 'offer_status',
    type: 'text',
    labelKey: 'da_col_offer_status',
    source: 'da_source_offer_status',
    emptyMeaning: 'da_null_offer_status',
  },
  {
    id: 'agent_id',
    type: 'text',
    labelKey: 'da_col_agent_id',
    source: 'da_source_agent_id',
    emptyMeaning: 'da_null_agent_id',
    pii: true,
  },
  {
    id: 'agent_code',
    type: 'text',
    labelKey: 'da_col_agent_code',
    source: 'da_source_agent_code',
    emptyMeaning: 'da_null_agent_code',
    pii: true,
  },
  {
    id: 'agent_name',
    type: 'text',
    labelKey: 'da_col_agent_name',
    source: 'da_source_agent_name',
    emptyMeaning: 'da_null_agent_name',
    pii: true,
  },
  {
    id: 'agent_phone',
    type: 'text',
    labelKey: 'da_col_agent_phone',
    source: 'da_source_agent_phone',
    emptyMeaning: 'da_null_agent_phone',
    pii: true,
  },
  {
    id: 'dispatch_mode',
    type: 'text',
    labelKey: 'da_col_dispatch_mode',
    source: 'da_source_dispatch_mode',
    emptyMeaning: 'da_null_dispatch_mode',
  },
  {
    id: 'strategy',
    type: 'text',
    labelKey: 'da_col_strategy',
    source: 'da_source_strategy',
    emptyMeaning: 'da_null_strategy',
  },
  {
    id: 'search_id',
    type: 'text',
    labelKey: 'da_col_search_id',
    source: 'da_source_search_id',
    emptyMeaning: 'da_null_search_id',
  },
  {
    id: 'search_number',
    type: 'number',
    labelKey: 'da_col_search_number',
    source: 'da_source_search_number',
    emptyMeaning: 'da_null_search_number',
  },
  {
    id: 'search_purpose',
    type: 'text',
    labelKey: 'da_col_search_purpose',
    source: 'da_source_search_purpose',
    emptyMeaning: 'da_null_search_purpose',
  },
  {
    id: 'search_state',
    type: 'text',
    labelKey: 'da_col_search_state',
    source: 'da_source_search_state',
    emptyMeaning: 'da_null_search_state',
  },
  {
    id: 'attempt_id',
    type: 'text',
    labelKey: 'da_col_attempt_id',
    source: 'da_source_attempt_id',
    emptyMeaning: 'da_null_attempt_id',
  },
  {
    id: 'attempt_revision',
    type: 'number',
    labelKey: 'da_col_attempt_revision',
    source: 'da_source_attempt_revision',
    emptyMeaning: 'da_null_attempt_revision',
  },
  {
    id: 'attempt_schema_version',
    type: 'number',
    labelKey: 'da_col_attempt_schema_version',
    source: 'da_source_attempt_schema_version',
    emptyMeaning: 'da_null_attempt_schema_version',
  },
  {
    id: 'attempt_profile',
    type: 'text',
    labelKey: 'da_col_attempt_profile',
    source: 'da_source_attempt_profile',
    emptyMeaning: 'da_null_attempt_profile',
  },
  {
    id: 'attempt_trigger',
    type: 'text',
    labelKey: 'da_col_attempt_trigger',
    source: 'da_source_attempt_trigger',
    emptyMeaning: 'da_null_attempt_trigger',
  },
  {
    id: 'attempt_computed_at',
    type: 'timestamp',
    labelKey: 'da_col_attempt_computed_at',
    source: 'da_source_attempt_computed_at',
    emptyMeaning: 'da_null_attempt_computed_at',
  },
  {
    id: 'attempt_area_id',
    type: 'text',
    labelKey: 'da_col_attempt_area_id',
    source: 'da_source_attempt_area_id',
    emptyMeaning: 'da_null_attempt_area_id',
  },
  {
    id: 'candidate_evaluation_id',
    type: 'text',
    labelKey: 'da_col_candidate_evaluation_id',
    source: 'da_source_candidate_evaluation_id',
    emptyMeaning: 'da_null_candidate_evaluation_id',
  },
  {
    id: 'policy_version_id',
    type: 'text',
    labelKey: 'da_col_policy_version_id',
    source: 'da_source_policy_version_id',
    emptyMeaning: 'da_null_policy_version_id',
    meaningKey: 'da_meaning_policy_version_id',
  },
  {
    id: 'policy_source',
    type: 'text',
    labelKey: 'da_col_policy_source',
    source: 'da_source_policy_source',
    emptyMeaning: 'da_null_policy_source',
  },
  {
    id: 'policy_scope_id',
    type: 'text',
    labelKey: 'da_col_policy_scope_id',
    source: 'da_source_policy_scope_id',
    emptyMeaning: 'da_null_policy_scope_id',
  },
  {
    id: 'policy_version_ids',
    type: 'list',
    labelKey: 'da_col_policy_version_ids',
    source: 'da_source_policy_version_ids',
    emptyMeaning: 'da_null_policy_version_ids',
  },
  {
    id: 'policy_sources',
    type: 'list',
    labelKey: 'da_col_policy_sources',
    source: 'da_source_policy_sources',
    emptyMeaning: 'da_null_policy_sources',
  },
  {
    id: 'offer_round',
    type: 'number',
    labelKey: 'da_col_offer_round',
    source: 'da_source_offer_round',
    emptyMeaning: 'da_null_offer_round',
  },
  {
    id: 'offer_sequence',
    type: 'number',
    labelKey: 'da_col_offer_sequence',
    source: 'da_source_offer_sequence',
    emptyMeaning: 'da_null_offer_sequence',
  },
  {
    id: 'offer_rank',
    type: 'number',
    labelKey: 'da_col_offer_rank',
    source: 'da_source_offer_rank',
    emptyMeaning: 'da_null_offer_rank',
  },
  {
    id: 'candidate_score',
    type: 'number',
    labelKey: 'da_col_candidate_score',
    source: 'da_source_candidate_score',
    emptyMeaning: 'da_null_candidate_score',
    meaningKey: 'da_meaning_candidate_score',
  },
  {
    id: 'distance_to_pickup_km',
    type: 'distance',
    labelKey: 'da_col_distance_to_pickup_km',
    source: 'da_source_distance_to_pickup_km',
    emptyMeaning: 'da_null_distance_to_pickup_km',
  },
  {
    id: 'offered_at',
    type: 'timestamp',
    labelKey: 'da_col_offered_at',
    source: 'da_source_offered_at',
    emptyMeaning: 'da_null_offered_at',
  },
  {
    id: 'expires_at',
    type: 'timestamp',
    labelKey: 'da_col_expires_at',
    source: 'da_source_expires_at',
    emptyMeaning: 'da_null_expires_at',
  },
  {
    id: 'responded_at',
    type: 'timestamp',
    labelKey: 'da_col_responded_at',
    source: 'da_source_responded_at',
    emptyMeaning: 'da_null_responded_at',
  },
  {
    id: 'confirmed_at',
    type: 'timestamp',
    labelKey: 'da_col_confirmed_at',
    source: 'da_source_confirmed_at',
    emptyMeaning: 'da_null_confirmed_at',
    meaningKey: 'da_meaning_confirmed_at',
  },
  {
    id: 'reason_id',
    type: 'text',
    labelKey: 'da_col_reason_id',
    source: 'da_source_reason_id',
    emptyMeaning: 'da_null_reason_id',
  },
  {
    id: 'reason_code',
    type: 'text',
    labelKey: 'da_col_reason_code',
    source: 'da_source_reason_code',
    emptyMeaning: 'da_null_reason_code',
  },
  {
    id: 'reason_label',
    type: 'text',
    labelKey: 'da_col_reason_label',
    source: 'da_source_reason_label',
    emptyMeaning: 'da_null_reason_label',
  },
  {
    id: 'reason_note',
    type: 'text',
    labelKey: 'da_col_reason_note',
    source: 'da_source_reason_note',
    emptyMeaning: 'da_null_reason_note',
    pii: true,
  },
  {
    id: 'initiated_by_id',
    type: 'text',
    labelKey: 'da_col_initiated_by_id',
    source: 'da_source_initiated_by_id',
    emptyMeaning: 'da_null_initiated_by_id',
    pii: true,
  },
  {
    id: 'confirmation_actor_type',
    type: 'text',
    labelKey: 'da_col_confirmation_actor_type',
    source: 'da_source_confirmation_actor_type',
    emptyMeaning: 'da_null_confirmation_actor_type',
  },
  {
    id: 'confirmation_actor_id',
    type: 'text',
    labelKey: 'da_col_confirmation_actor_id',
    source: 'da_source_confirmation_actor_id',
    emptyMeaning: 'da_null_confirmation_actor_id',
    pii: true,
  },
  {
    id: 'confirmation_actor_name',
    type: 'text',
    labelKey: 'da_col_confirmation_actor_name',
    source: 'da_source_confirmation_actor_name',
    emptyMeaning: 'da_null_confirmation_actor_name',
    pii: true,
  },
  {
    id: 'from_agent_id',
    type: 'text',
    labelKey: 'da_col_from_agent_id',
    source: 'da_source_from_agent_id',
    emptyMeaning: 'da_null_from_agent_id',
    pii: true,
  },
  {
    id: 'to_agent_id',
    type: 'text',
    labelKey: 'da_col_to_agent_id',
    source: 'da_source_to_agent_id',
    emptyMeaning: 'da_null_to_agent_id',
    pii: true,
  },
  {
    id: 'confirmation_outcome',
    type: 'text',
    labelKey: 'da_col_confirmation_outcome',
    source: 'da_source_confirmation_outcome',
    emptyMeaning: 'da_null_confirmation_outcome',
    meaningKey: 'da_meaning_confirmation_outcome',
  },
  {
    id: 'evidence_state',
    type: 'text',
    labelKey: 'da_col_evidence_state',
    source: 'da_source_evidence_state',
    emptyMeaning: 'da_null_evidence_state',
  },
  {
    id: 'evidence_expired_at',
    type: 'timestamp',
    labelKey: 'da_col_evidence_expired_at',
    source: 'da_source_evidence_expired_at',
    emptyMeaning: 'da_null_evidence_expired_at',
  },
  {
    id: 'scope_provenance',
    type: 'text',
    labelKey: 'da_col_scope_provenance',
    source: 'da_source_scope_provenance',
    emptyMeaning: 'da_null_scope_provenance',
  },
] as const satisfies readonly ReportTableColumn[];
export const driverAssignmentDetailDefaultColumns = [
  'provider_name',
  'trip_ref',
  'row_kind',
  'event_at',
  'agent_name',
  'dispatch_mode',
  'strategy',
  'offer_kind',
  'offer_status',
  'offer_rank',
  'candidate_score',
  'confirmation_actor_name',
  'confirmation_outcome',
  'evidence_state',
] as const;
export const driverAssignmentDetailDateBasis = 'OFFER_OR_ASSIGNMENT_TIME';
export const driverAssignmentDetailSpecificFilters = [
  { id: 'rowKinds', type: 'multi', values: ['OFFER', 'ASSIGNMENT_EVENT'] },
  {
    id: 'offerKinds',
    type: 'multi',
    values: ['LEGACY_HISTORY', 'AUTO_INITIAL', 'MANUAL_INITIAL', 'MANUAL_REASSIGNMENT'],
  },
  {
    id: 'offerStatuses',
    type: 'multi',
    values: ['PENDING', 'ACCEPTED', 'REJECTED', 'EXPIRED', 'SUPERSEDED', 'CANCELLED'],
  },
  { id: 'dispatchModes', type: 'multi', values: ['AUTO', 'MANUAL'] },
  { id: 'strategies', type: 'multi', values: ['SEQUENTIAL', 'BROADCAST'] },
  { id: 'searchPurposes', type: 'multi', values: ['INITIAL', 'REASSIGNMENT'] },
  {
    id: 'evidenceStates',
    type: 'multi',
    values: ['LEGACY_HISTORY', 'AVAILABLE', 'MISSING', 'EXPIRED', 'UNSUPPORTED'],
  },
  { id: 'agentIds', type: 'uuidMulti' },
  { id: 'searchId', type: 'uuid' },
  { id: 'reasonCode', type: 'text' },
] as const satisfies readonly ReportSpecificFilter[];
export const driverAssignmentDetailSummaryFields = [
  'rowCount',
  'distinctTripCount',
  'offerCount',
  'acceptedOfferCount',
  'rejectedOfferCount',
  'expiredOfferCount',
  'supersededOfferCount',
  'cancelledOfferCount',
  'pendingOfferCount',
  'confirmationOnlyCount',
  'manualCount',
  'reassignmentCount',
  'confirmedCount',
  'ambiguousConfirmationCount',
] as const;
